import _ from 'lodash';
import { Ty } from '../ty';

export type StructTypeConstraint = {
  readonly t: 'struct';
  readonly k: 'constraint';
  readonly fields: {
    readonly [name: string]: {
      readonly ty: TypeConstraint;
      readonly optional: boolean;
    };
  };
};

export type RecordTypeConstraint = {
  readonly t: 'record';
  readonly k: 'constraint';
  readonly allowed: TypeConstraint;
};

export type ArrayTypeConstraint = {
  readonly t: 'array';
  readonly k: 'constraint';
  readonly allowed: TypeConstraint;
};

export type ConcreteTypeConstraint = {
  readonly t: 'concrete-type';
  readonly k: 'constraint';
  readonly ty: Ty.ExtendedAttributeType;
};

export type TypeConstraintItem = (
  | ConcreteTypeConstraint
  | RecordTypeConstraint
  | StructTypeConstraint
  | ArrayTypeConstraint
) & {
  meta?: {
    description?: string;
  };
};

export type TypeConstraint = 'any' | readonly TypeConstraintItem[];

/**
 * Displays a TypeConstraint as a string.
 * - If the constraint is 'any', returns "any".
 * - If it's an array of AllowedItem, joins their string representations with " | ".
 */
export const displayTypeConstraint = (constraint: TypeConstraint): string => {
  if (constraint === 'any') {
    return 'any';
  }
  return constraint.map(displayTypeConstraintItem).join(' | ');
};

/**
 * Displays a single AllowedItem as a string.
 * This function mirrors the structure of displayTy but for AllowedItem.
 */
const displayTypeConstraintItem = (item: TypeConstraintItem): string => {
  switch (item.t) {
    case 'concrete-type':
      // Use displayTy by wrapping the concrete type in an object similar to Shorthand output.
      return Ty.displayTy(item.ty);

    case 'array':
      return `ARRAY(${displayTypeConstraint(item.allowed)})`;

    case 'struct': {
      const fieldsDisplay = Object.entries(item.fields)
        .map(
          ([name, fieldItem]) =>
            `${Ty.escapeStructKey(name)}${
              fieldItem.optional ? '?' : ''
            }: ${displayTypeConstraint(fieldItem.ty)}`
        )
        .join(', ');
      return `STRUCT({ ${fieldsDisplay} })`;
    }

    case 'record':
      return `RECORD(${displayTypeConstraint(item.allowed)})`;

    default:
      return 'unknown';
  }
};

const shorthandToTypeConstraintItem = (
  sh: Ty.Shorthand
): TypeConstraintItem => {
  // 1) If Shorthand is a string => it's a PrimitiveAttributeType
  if (typeof sh === 'string') {
    return {
      t: 'concrete-type',
      k: 'constraint',
      ty: Ty.shorthandToTy(sh),
    };
  }

  if ('k' in sh) {
    switch (sh.k) {
      case 'array':
        return {
          t: 'array',
          k: 'constraint',
          // In this example, we treat the single "t" as one possible allowed item
          allowed: [shorthandToTypeConstraintItem(sh.t)],
        };

      case 'struct':
        return {
          t: 'struct',
          k: 'constraint',
          fields: _.mapValues(sh.fields, (fieldSh) => ({
            ty: [shorthandToTypeConstraintItem(fieldSh)],
            optional: false,
          })),
        };

      default:
        // For any unhandled k (like 'range', 'enum' etc.)
        // treat them as a concrete type
        return {
          t: 'concrete-type',
          k: 'constraint',
          ty: Ty.shorthandToTy(sh),
        };
    }
  }

  // 3) If it's an object with a "ty" field or an ExtendedAttributeType,
  //    treat it as a concrete type
  if ('ty' in sh) {
    return {
      t: 'concrete-type',
      k: 'constraint',
      ty: Ty.shorthandToTy(sh),
    };
  }

  throw new Error(`Unrecognized shorthand variant: ${JSON.stringify(sh)}`);
};

/**
 * Helper: Convert `'any' | Ty.Shorthand[]` into `'any' | AllowedItem[]`.
 */
export const convertShorthandArrayToTypeConstraint = (
  allowed: 'any' | Ty.Shorthand[]
): TypeConstraint => {
  if (allowed === 'any') {
    return 'any';
  }
  // Otherwise, map each shorthand -> AllowedItem
  return allowed.map((shorthand) => shorthandToTypeConstraintItem(shorthand));
};
